import { useState } from "react";
import { Loader2, Send } from "lucide-react";
import { toast } from "sonner";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from "@bookdental/ui";
import { trpc } from "@/lib/trpc";
import { AJUDA_PROTESE } from "@/lib/ajuda/dialogos/protese";
import { FilesDropzone } from "@/components/files/FilesDropzone";

export type ReworkTarget = {
  serviceId: string;
  serviceLabel: string;
  patientName: string;
} | null;

/** Quem está abrindo. Muda só a linguagem — o efeito no servidor é o mesmo evento. */
type Lado = "clinica" | "laboratorio";

/** Modal de retrabalho — observação obrigatória + anexos (reaproveita o módulo de Arquivos
 *  genérico: cada arquivo solto já fica relacionado ao serviço na hora, antes mesmo de enviar). Ao
 *  confirmar, o serviço reabre pra "Em Produção" e o OUTRO lado é avisado — retrabalho é um evento
 *  (`ProsthesisReworkRequest`), não um status — ver `prosthesis.requestRework`.
 *
 *  O mesmo diálogo serve às duas pontas. Do lado do laboratório não é "solicitar" nada: ele é quem
 *  vai refazer, então o texto fala de reabrir o serviço, não de pedir. */
export function RequestReworkDialog({
  target,
  lado = "clinica",
  onClose,
}: {
  target: ReworkTarget;
  lado?: Lado;
  onClose: () => void;
}) {
  const ehLab = lado === "laboratorio";
  const utils = trpc.useUtils();
  const [notes, setNotes] = useState("");

  function handleClose() {
    setNotes("");
    onClose();
  }

  const attachToEntity = trpc.file.attachToEntity.useMutation();

  const requestRework = trpc.prosthesis.requestRework.useMutation({
    onSuccess: () => {
      toast.success(ehLab ? "Serviço reaberto pra refazer." : "Retrabalho solicitado ao laboratório.");
      // status muda (volta pra EM_PRODUCAO) — invalida os dois lados + contadores do dashboard.
      utils.prosthesis.list.invalidate();
      utils.prosthesis.getById.invalidate();
      utils.prosthesis.countByStatus.invalidate();
      utils.prosthesis.incoming.list.invalidate();
      utils.prosthesis.incoming.getById.invalidate();
      handleClose();
    },
    onError: (e) => toast.error(e.message || "Erro ao solicitar retrabalho."),
  });

  function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    if (!target) return;
    if (notes.trim().length < 3) {
      toast.error("Descreva o que precisa ser ajustado.");
      return;
    }
    requestRework.mutate({ serviceId: target.serviceId, notes: notes.trim() });
  }

  return (
    <Dialog open={!!target} onOpenChange={(v) => !v && !requestRework.isPending && handleClose()}>
      <DialogContent className="max-w-lg dark:bg-card">
        <DialogHeader>
          <DialogTitle ajuda={ehLab ? AJUDA_PROTESE.refazerServico : AJUDA_PROTESE.solicitarRetrabalho}>
            {ehLab ? "Refazer serviço" : "Solicitar retrabalho"}
          </DialogTitle>
          <DialogDescription>
            {target && `${target.patientName} · ${target.serviceLabel}`}
          </DialogDescription>
        </DialogHeader>

        <form onSubmit={handleSubmit} className="space-y-4">
          <div>
            <label className="mb-1 block text-xs font-medium text-zinc-600 dark:text-muted-foreground">
              {ehLab ? "O que precisa ser refeito? *" : "O que precisa ser ajustado? *"}
            </label>
            <textarea
              value={notes}
              onChange={(e) => setNotes(e.target.value)}
              rows={4}
              autoFocus
              placeholder={
                ehLab
                  ? "Descreva o motivo — a clínica é avisada de que o serviço voltou pra produção..."
                  : "Descreva o motivo do retrabalho e o que o laboratório precisa corrigir..."
              }
              className="w-full resize-none rounded-md border border-zinc-200 bg-white px-3 py-2 text-sm text-zinc-900 placeholder:text-zinc-400 focus:border-brand-500 focus:outline-none focus:ring-1 focus:ring-brand-500 dark:border-border dark:bg-card dark:text-foreground"
            />
          </div>

          <div>
            <label className="mb-1 block text-xs font-medium text-zinc-600 dark:text-muted-foreground">
              Fotos, escaneamentos (STL/PLY) ou exames
            </label>
            {target && (
              <FilesDropzone
                kind="GENERIC"
                onUploaded={(file) =>
                  attachToEntity.mutate({ fileId: file.fileId, relatedType: "PROSTHESIS_SERVICE", relatedId: target.serviceId })
                }
              />
            )}
          </div>

          <div className="flex justify-end gap-2 border-t border-zinc-100 pt-3 dark:border-border">
            <button
              type="button"
              onClick={handleClose}
              disabled={requestRework.isPending}
              className="inline-flex h-9 items-center rounded-md border border-zinc-200 px-3 text-[13px] font-medium text-zinc-700 transition-colors hover:bg-zinc-50 disabled:opacity-60 dark:border-border dark:text-foreground dark:hover:bg-muted"
            >
              Cancelar
            </button>
            <button
              type="submit"
              disabled={requestRework.isPending}
              className="inline-flex h-9 items-center gap-1.5 rounded-md bg-red-600 px-4 text-[13px] font-semibold text-white shadow-sm transition-colors hover:bg-red-700 disabled:opacity-60"
            >
              {requestRework.isPending ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Send className="h-3.5 w-3.5" />}
              Enviar solicitação
            </button>
          </div>
        </form>
      </DialogContent>
    </Dialog>
  );
}
